// #935 PR-C2 — client-side chunked, re-entrant dispatch loop.
//
// The rebuilt run endpoint (`POST /api/dispatch/run { date }`) is chunked +
// re-entrant: each call dispatches at most one chunk and returns the
// UNWRAPPED `DispatchRunResult` = `{ date, dispatched, skipped, remaining }`.
// The caller MUST loop, re-POSTing until `remaining === 0`, accumulating
// `dispatched` + `skipped` across chunks. Re-running is idempotent (a job is
// eligible iff planned AND not yet dispatched), so a retry never
// double-dispatches.
//
// This is the load-bearing behaviour for the run action, extracted as a pure
// function so the loop / accumulation / progress-guard is unit-testable
// without rendering the dashboard. The HTTP call is injected.

/** Bare response shape of `POST /api/dispatch/run` (`DispatchRunResult`). */
export interface DispatchRunChunkResult {
  date: string;
  dispatched: number;
  /** Groups intentionally not dispatched this chunk (cancelled / not found). */
  skipped: number;
  /** Eligible groups not yet processed after this call. */
  remaining: number;
}

/** Accumulated outcome across all chunks of one operator-triggered run. */
export interface DispatchRunAccumulated {
  date: string;
  /** Total groups dispatched across every chunk. */
  dispatched: number;
  /** Total groups skipped across every chunk. */
  skipped: number;
  /**
   * Eligible groups still NOT processed when the loop stopped. 0 on a clean
   * drain; > 0 when the loop stopped early because a chunk made no forward
   * progress (e.g. every remaining group's Onfleet create failed).
   */
  remaining: number;
  /** Number of chunk calls made (>= 1). */
  chunks: number;
}

/** Injected progress callback — fired after each chunk with running totals. */
export type DispatchRunProgress = (snapshot: {
  dispatched: number;
  skipped: number;
  remaining: number;
  chunks: number;
}) => void;

/**
 * Drive the re-entrant dispatch loop to completion.
 *
 * Loops `post({ date })` until `remaining === 0`, accumulating `dispatched`
 * and `skipped`.
 *
 * Progress guard: if a chunk neither dispatches nor skips any group while
 * `remaining > 0` (every group's Onfleet create failed, or only wedged rows
 * are left), re-POSTing would spin forever. We stop after such a no-progress
 * chunk and surface the residual `remaining` so the operator sees the day did
 * not fully drain (and can retry after fixing the upstream fault). This is
 * fail-loud, not a swallow: a non-zero `remaining` is a partial result, and a
 * thrown error (a chunk POST rejecting) propagates unchanged — we never catch
 * it.
 */
export async function runDispatchChunked(
  post: (body: { date: string }) => Promise<DispatchRunChunkResult>,
  date: string,
  onProgress?: DispatchRunProgress,
): Promise<DispatchRunAccumulated> {
  let dispatched = 0;
  let skipped = 0;
  let chunks = 0;
  let remaining = 0;

  for (;;) {
    // A failing chunk POST rejects here and propagates to the caller — no
    // catch, no swallow. The server already persisted any earlier chunks'
    // dispatches; a retry is idempotent, so the operator can safely re-run
    // for the same date.
    const res = await post({ date });
    chunks += 1;
    dispatched += res.dispatched;
    skipped += res.skipped;
    remaining = res.remaining;

    onProgress?.({ dispatched, skipped, remaining, chunks });

    if (remaining === 0) break;

    // No forward progress this chunk (nothing dispatched or skipped) but work
    // remains: stop rather than spin. The residual `remaining` is surfaced to
    // the operator as a partial result.
    if (res.dispatched === 0 && res.skipped === 0) break;
  }

  return { date, dispatched, skipped, remaining, chunks };
}
